/**
 * lightbox - the GALLERY composition renderer (docs/libs/lightbox.md
 * "The gallery composition", upper #38): a `files` gallery jsonb array
 * (file-selekt's stored shape) becomes the plate-grid markup — figure >
 * a.gallery-plate[lightbox=group] > img.gallery-thumb + the zoom badge,
 * plus the figcaption. The caller injects the string and (outside lister
 * loads, which re-scan by themselves) calls g.lightbox.refresh().
 *
 * URL rule = file-selekt's display rule: absolute paths pass through
 * (CDN), everything else prefixes g.settings.filesUrl. The overlay opens
 * the CROPPED variant when one exists (what the user framed), else the
 * original; the plate prefers the thumb variant.
 */

let uid = 0; // per-CALL default group — one call renders one entity

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
	{ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

function url(path) {
	if (!path) return '';
	// absolute (CDN) and root-relative (already-resolved) paths pass through;
	// a bare stored path prefixes the files base, like every file display
	if (/^https?:\/\//.test(path) || path.startsWith('/')) return path;
	const base = String(((window.g && window.g.settings) || {}).filesUrl || '').replace(/\/+$/, '');
	return base + '/' + path;
}

/**
 * gallery(files, options) → the markup STRING.
 *   files    one file jsonb or an array (stored order = display order —
 *            the cover flag marks the entity's representative image, it
 *            never reorders the grid)
 *   options  group    the lightbox group value — per ENTITY (the law:
 *                     'material-12', never a page-wide constant); absent =
 *                     a unique per-call group, which IS per-entity when
 *                     each entity renders with its own call
 *            captions false = skip figcaptions even when captions exist
 */
export function gallery(files, options) {
	const opts = options || {};
	const list = (Array.isArray(files) ? files : [files]).filter((f) => f && f.path);
	if (!list.length) return '';
	const group = opts.group || 'gallery-' + (++uid);
	const t = window.g ? window.g.translate.bind(window.g) : ((s) => s);
	const plates = list.map((f) => {
		const variants = f.variants || {};
		const full = url((variants.cropped && variants.cropped.path) || f.path);
		const thumb = url((variants.thumb && variants.thumb.path) || (variants.cropped && variants.cropped.path) || f.path);
		const caption = f.caption ? String(f.caption) : '';
		return '<figure>'
			+ `<a class="gallery-plate" href="${esc(full)}" lightbox="${esc(group)}"`
			+ (caption ? ` lightbox-title="${esc(caption)}"` : '')
			+ ` aria-label="${esc(t('Open the full image'))}">`
			+ `<img class="gallery-thumb" src="${esc(thumb)}" alt="${esc(caption || f.name || '')}" loading="lazy">`
			+ '<span class="gallery-plate-zoom" aria-hidden="true"><i class="ri-eye-line" aria-hidden="true"></i></span>'
			+ '</a>'
			+ (caption && opts.captions !== false ? `<figcaption>${esc(caption)}</figcaption>` : '')
			+ '</figure>';
	});
	return `<div class="gallery">${plates.join('')}</div>`;
}
